import React from 'react';
import { FileText, ArrowRight } from 'lucide-react';
import { ContractFromAPI } from '../../services/api';
import { Contract } from '../contracts';
import { StatusBadge, ContractStatus } from '../../lib/contract-status';

export interface RecentContractsListProps {
  contracts: ContractFromAPI[];
  onViewContract: (contract: Contract) => void;
  onViewAll?: () => void;
  loading?: boolean;
  maxItems?: number;
}

const ContractItemSkeleton: React.FC = () => (
  <div className="flex items-center space-x-3 p-3 animate-pulse">
    <div className="h-9 w-9 rounded-lg bg-neutral-200 flex-shrink-0" />
    <div className="flex-1 space-y-2">
      <div className="h-4 w-3/4 bg-neutral-200 rounded" />
      <div className="h-3 w-1/3 bg-neutral-200 rounded" />
    </div>
    <div className="h-5 w-16 bg-neutral-200 rounded-full flex-shrink-0" />
  </div>
);

const RecentContractsListSkeleton: React.FC<{ count?: number }> = ({ count = 5 }) => (
  <div
    className="bg-white rounded-xl shadow-card border border-neutral-100 p-6"
    data-testid="recent-contracts-skeleton"
  >
    <div className="h-6 w-40 bg-neutral-200 rounded mb-4 animate-pulse" />
    <div className="space-y-1">
      {Array.from({ length: count }).map((_, i) => (
        <ContractItemSkeleton key={i} />
      ))}
    </div>
  </div>
);

function formatRelativeTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function mapToContract(c: ContractFromAPI): Contract {
  return {
    id: c.id,
    name: c.name,
    state: c.state,
    status: c.status as ContractStatus,
    effective_date: c.effective_date || '',
    expiration_date: c.expiration_date || undefined,
    version: c.version || undefined,
    created_at: c.created_at,
    updated_at: c.updated_at,
    analysis_status: c.analysis_status,
    gap_count: c.gap_count,
  };
}

const RecentContractsList: React.FC<RecentContractsListProps> = ({
  contracts,
  onViewContract,
  onViewAll,
  loading = false,
  maxItems = 5,
}) => {
  if (loading) {
    return <RecentContractsListSkeleton count={maxItems} />;
  }

  const displayed = contracts.slice(0, maxItems);

  if (contracts.length === 0) {
    return (
      <div
        className="bg-white rounded-xl shadow-card border border-neutral-100 p-6
                   transition-all duration-200 ease-in-out hover:shadow-card-hover"
        data-testid="recent-contracts-section"
      >
        <h3 className="text-lg font-semibold text-neutral-900 mb-4">Recent Contracts</h3>
        <div
          className="flex items-center justify-center h-40 text-neutral-400"
          data-testid="contracts-empty-state"
        >
          <div className="text-center">
            <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p className="text-sm font-medium">No contracts yet</p>
            <p className="text-xs text-neutral-400 mt-1">Upload one to get started</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-white rounded-xl shadow-card border border-neutral-100 p-6
                 transition-all duration-200 ease-in-out hover:shadow-card-hover"
      data-testid="recent-contracts-section"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-neutral-900">Recent Contracts</h3>
        {contracts.length > maxItems && onViewAll && (
          <button
            onClick={onViewAll}
            className="text-sm font-medium text-primary-600 hover:text-primary-700
                       transition-colors px-2 py-1 rounded hover:bg-primary-50
                       focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                       inline-flex items-center gap-1"
            data-testid="view-all-contracts"
          >
            View all
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <div className="space-y-1" data-testid="recent-contracts-list">
        {displayed.map((contract) => (
          <button
            key={contract.id}
            onClick={() => onViewContract(mapToContract(contract))}
            className="w-full flex items-center space-x-3 p-3 rounded-lg
                       hover:bg-neutral-50 transition-all duration-150
                       cursor-pointer group text-left"
            data-testid={`recent-contract-${contract.id}`}
          >
            <div
              className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0
                         bg-secondary-100 text-secondary-600
                         transition-transform duration-200 group-hover:scale-105"
            >
              <FileText className="h-4 w-4" />
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-neutral-900 truncate">
                {contract.name}
              </p>
              <p className="text-xs text-neutral-500 mt-0.5">
                {contract.state} &middot; {formatRelativeTime(contract.created_at)}
              </p>
            </div>

            <div className="flex-shrink-0">
              <StatusBadge status={contract.status as ContractStatus} />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default RecentContractsList;
